import { spawn } from "node:child_process";
import { parse as parseYaml } from "yaml";
import { LocalCliError } from "./errors.js";
import { signAppJwt } from "@m8t-stack/github-app-auth";

/**
 * True iff `.m8t/brain.yaml` text identifies a reusable m8t brain: it carries
 * the AUTHORITATIVE engine/processes block (present from the template's initial
 * push, before linking) or a `link:` mirror block. Does NOT require a resolved
 * `agents:` value — that is `{{AGENT}}` on a freshly-pushed-but-unlinked repo.
 * Malformed or empty text → false (never treat an unparseable file as a brain).
 */
export function isM8tBrainMarker(yamlText: string): boolean {
  if (!yamlText.trim()) return false;
  let parsed: unknown;
  try {
    parsed = parseYaml(yamlText);
  } catch {
    return false;
  }
  if (!parsed || typeof parsed !== "object") return false;
  const root = parsed as Record<string, unknown>;
  const isObj = (v: unknown): boolean => typeof v === "object" && v !== null;
  return isObj(root.engine) || isObj(root.processes) || isObj(root.link);
}

export async function createBlankRepo(args: {
  token: string;
  org: string;
  name: string;
  private?: boolean;
}): Promise<{ cloneUrl: string; defaultBranch: string }> {
  const res = await fetch(`https://api.github.com/orgs/${args.org}/repos`, {
    method: "POST",
    headers: {
      Authorization: `token ${args.token}`,
      Accept: "application/vnd.github+json",
      "X-GitHub-Api-Version": "2022-11-28",
      "User-Agent": "m8t",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ name: args.name, private: args.private !== false, auto_init: false }),
  });
  const text = await res.text();
  if (!res.ok) {
    throw new LocalCliError({
      code: "GH_APP_REPO_CREATE_FAILED",
      message: `POST /orgs/${args.org}/repos HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
    });
  }
  const j = JSON.parse(text) as { clone_url: string; default_branch?: string };
  return { cloneUrl: j.clone_url, defaultBranch: j.default_branch ?? "main" };
}

function run(cmd: string, cmdArgs: string[], cwd: string, env?: NodeJS.ProcessEnv): Promise<void> {
  return new Promise((resolve, reject) => {
    const c = spawn(cmd, cmdArgs, { cwd, env: { ...process.env, ...env }, stdio: ["ignore", "pipe", "pipe"] });
    let err = "";
    c.stderr.on("data", (d: Buffer) => {
      err += d.toString();
    });
    // Without this, a spawn failure (ENOENT/EAGAIN — e.g. the binary is missing or
    // the container can't fork) emits an unhandled 'error' event that Node rethrows as
    // an UNCAUGHT exception — a non-JSON stack trace that escapes the CLI's --output
    // json envelope. Convert it to a LocalCliError so it surfaces cleanly like any other.
    c.on("error", (e: Error) => {
      reject(
        new LocalCliError({
          code: "GH_APP_PUSH_FAILED",
          message: `${cmd} ${cmdArgs.join(" ")} could not run: ${e.message}`,
        }),
      );
    });
    c.on("close", (code) => {
      if (code === 0) {
        resolve();
      } else {
        reject(
          new LocalCliError({
            code: "GH_APP_PUSH_FAILED",
            message: `${cmd} ${cmdArgs.join(" ")} failed: ${err.slice(0, 300)}`,
          }),
        );
      }
    });
  });
}

/**
 * Push the contents of `sourceDir` (a rendered brain-template+seed) to a freshly
 * created blank repo using an installation token (embedded via x-access-token).
 *
 * Pass `remoteOverride` to substitute the computed https remote — useful in tests
 * so no network or real token is needed (e.g. a `file://` path to a local bare repo).
 */
export async function pushSeed(args: {
  token: string;
  org: string;
  name: string;
  branch: string;
  sourceDir: string;
  remoteOverride?: string;
}): Promise<void> {
  const remote =
    args.remoteOverride ?? `https://x-access-token:${args.token}@github.com/${args.org}/${args.name}.git`;
  const env: NodeJS.ProcessEnv = {
    GIT_AUTHOR_NAME: "m8t",
    GIT_AUTHOR_EMAIL: "m8t@users.noreply.github.com",
    GIT_COMMITTER_NAME: "m8t",
    GIT_COMMITTER_EMAIL: "m8t@users.noreply.github.com",
  };
  await run("git", ["init", "-q", "-b", args.branch], args.sourceDir, env);
  await run("git", ["add", "-A"], args.sourceDir, env);
  await run("git", ["commit", "-q", "-m", "seed: initial brain"], args.sourceDir, env);
  await run("git", ["remote", "add", "origin", remote], args.sourceDir, env);
  await run("git", ["push", "-q", "origin", args.branch], args.sourceDir, env);
}

const GH_API = "https://api.github.com";
const GH_REQUEST_TIMEOUT_MS = 30_000;
function ghHeaders(token: string, scheme: "token" | "Bearer" = "token"): Record<string, string> {
  return {
    Authorization: `${scheme} ${token}`,
    Accept: "application/vnd.github+json",
    "X-GitHub-Api-Version": "2022-11-28",
    "User-Agent": "m8t",
    "Content-Type": "application/json",
  };
}

/** Mint a GitHub App installation token from a local PEM (the no-KV, local-bootstrap path). */
export async function mintInstallationTokenFromPem(args: {
  appId: string; privateKeyPem: string; installationId: string; fetchImpl?: typeof fetch;
}): Promise<string> {
  const doFetch = args.fetchImpl ?? fetch;
  const jwt = signAppJwt({ appId: args.appId, privateKeyPem: args.privateKeyPem });
  const res = await doFetch(`${GH_API}/app/installations/${args.installationId}/access_tokens`, {
    method: "POST", headers: ghHeaders(jwt, "Bearer"), signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS),
  });
  const text = await res.text();
  if (!res.ok) {
    throw new LocalCliError({
      code: "GH_APP_TOKEN_MINT_FAILED",
      message: `POST /app/installations/${args.installationId}/access_tokens HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
    });
  }
  return (JSON.parse(text) as { token: string }).token;
}

/** Read a file's decoded text from a repo via the Contents API, or null if it doesn't exist. */
export async function readRepoFileViaApp(args: {
  token: string; repo: string; path: string; ref?: string; fetchImpl?: typeof fetch;
}): Promise<string | null> {
  const doFetch = args.fetchImpl ?? fetch;
  const q = args.ref ? `?ref=${encodeURIComponent(args.ref)}` : "";
  const res = await doFetch(`${GH_API}/repos/${args.repo}/contents/${args.path}${q}`, { headers: ghHeaders(args.token), signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
  if (res.status === 404) return null;
  const text = await res.text();
  if (!res.ok) {
    throw new LocalCliError({
      code: "GH_APP_FILE_READ_FAILED",
      message: `GET /repos/${args.repo}/contents/${args.path}${q} HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
    });
  }
  const data = JSON.parse(text) as { content?: string };
  return Buffer.from(data.content ?? "", "base64").toString("utf8");
}

/** The repo's FIRST commit SHA (the initial App-authored seed commit for repos we created).
 *  Follows the Link rel="last" header on a 1-per-page commit listing. Null when unresolvable. */
export async function getInitialSeedCommitSha(args: { token: string; repo: string; fetchImpl?: typeof fetch }): Promise<string | null> {
  const doFetch = args.fetchImpl ?? fetch;
  const H = ghHeaders(args.token);
  const first = await doFetch(`${GH_API}/repos/${args.repo}/commits?per_page=1`, { headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
  if (!first.ok) return null;
  const link = first.headers.get("link");
  const lastUrl = /<([^>]+)>;\s*rel="last"/.exec(link ?? "")?.[1];
  const url = lastUrl ?? `${GH_API}/repos/${args.repo}/commits?per_page=1`;
  const res = await doFetch(url, { headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
  if (!res.ok) return null;
  const commits = JSON.parse(await res.text()) as { sha?: string }[];
  return commits.length > 0 ? (commits[commits.length - 1].sha ?? null) : null;
}

export type FileChange = { path: string; content: string } | { path: string; delete: true };

/** Commit multiple files (upserts and/or deletions) in ONE commit to an existing repo on `branch` via the Git Data API. */
export async function commitFilesViaApp(args: {
  token: string; repo: string; branch: string;
  files: FileChange[]; message: string; fetchImpl?: typeof fetch;
}): Promise<void> {
  const doFetch = args.fetchImpl ?? fetch;
  const H = ghHeaders(args.token);
  const api = async <T>(p: string, init?: { method: string; body: string }): Promise<T> => {
    const res = await doFetch(`${GH_API}/repos/${args.repo}${p}`, init ? { ...init, headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) } : { headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
    const text = await res.text();
    if (!res.ok) {
      throw new LocalCliError({
        code: "GH_APP_COMMIT_FAILED",
        message: `${init?.method ?? "GET"} ${p} HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
      });
    }
    return (text ? JSON.parse(text) : {}) as T;
  };
  const ref = await api<{ object: { sha: string } }>(`/git/ref/heads/${args.branch}`);
  const baseCommitSha = ref.object.sha;
  const baseCommit = await api<{ tree: { sha: string } }>(`/git/commits/${baseCommitSha}`);
  const upserts = args.files.filter((f): f is { path: string; content: string } => !("delete" in f));
  const blobs = await Promise.all(upserts.map((f) =>
    api<{ sha: string }>(`/git/blobs`, { method: "POST", body: JSON.stringify({ content: f.content, encoding: "utf-8" }) })));
  const tree = args.files.map((f) => "delete" in f
    ? { path: f.path, mode: "100644", type: "blob", sha: null }
    : { path: f.path, mode: "100644", type: "blob", sha: blobs[upserts.indexOf(f)].sha });
  const newTree = await api<{ sha: string }>(`/git/trees`, { method: "POST", body: JSON.stringify({ base_tree: baseCommit.tree.sha, tree }) });
  const commit = await api<{ sha: string }>(`/git/commits`, {
    method: "POST", body: JSON.stringify({ message: args.message, tree: newTree.sha, parents: [baseCommitSha] }),
  });
  await api(`/git/refs/heads/${args.branch}`, { method: "PATCH", body: JSON.stringify({ sha: commit.sha }) });
}

/** Create `refs/heads/<branch>` at `fromSha`; if it already exists (422), force-repoint it. */
export async function createBranchViaApp(args: { token: string; repo: string; branch: string; fromSha: string; fetchImpl?: typeof fetch }): Promise<void> {
  const doFetch = args.fetchImpl ?? fetch;
  const H = ghHeaders(args.token);
  const res = await doFetch(`${GH_API}/repos/${args.repo}/git/refs`, {
    method: "POST", headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS),
    body: JSON.stringify({ ref: `refs/heads/${args.branch}`, sha: args.fromSha }),
  });
  if (res.ok) return;
  if (res.status === 422) {
    // already exists → re-point it to fromSha
    const patch = await doFetch(`${GH_API}/repos/${args.repo}/git/refs/heads/${args.branch}`, {
      method: "PATCH", headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS),
      body: JSON.stringify({ sha: args.fromSha, force: true }),
    });
    if (patch.ok) return;
    throw new LocalCliError({
      code: "GH_APP_BRANCH_FAILED",
      message: `PATCH refs/heads/${args.branch} HTTP ${patch.status.toString()}: ${(await patch.text()).slice(0, 300)}`,
    });
  }
  throw new LocalCliError({
    code: "GH_APP_BRANCH_FAILED",
    message: `POST /git/refs HTTP ${res.status.toString()}: ${(await res.text()).slice(0, 300)}`,
  });
}

/**
 * Commit `files` (upserts and/or deletions) as ONE commit PARENTED ON `baseSha` — the commit's
 * `base_tree` is `baseSha`'s tree, so the resulting branch state is exactly base + this changeset
 * (no stale files left over from a prior run) — then point `branch` at the new commit via
 * `createBranchViaApp` (create, or force-update if it exists). The branch moves DIRECTLY to a
 * commit that carries the changes, so it never transits an empty (branch==base) state — which is
 * what a two-step reset-branch-to-base-then-commit does, and which GitHub auto-closes an open PR
 * on. This is the seed-refresh branch primitive; re-runs rebuild the PR branch cleanly + idempotently.
 */
export async function commitFilesFromBaseViaApp(args: {
  token: string; repo: string; branch: string; baseSha: string;
  files: FileChange[]; message: string; fetchImpl?: typeof fetch;
}): Promise<void> {
  const doFetch = args.fetchImpl ?? fetch;
  const H = ghHeaders(args.token);
  const api = async <T>(p: string, init?: { method: string; body: string }): Promise<T> => {
    const res = await doFetch(`${GH_API}/repos/${args.repo}${p}`, init ? { ...init, headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) } : { headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
    const text = await res.text();
    if (!res.ok) {
      throw new LocalCliError({
        code: "GH_APP_COMMIT_FAILED",
        message: `${init?.method ?? "GET"} ${p} HTTP ${res.status.toString()}: ${text.slice(0, 300)}`,
      });
    }
    return (text ? JSON.parse(text) : {}) as T;
  };
  const baseCommit = await api<{ tree: { sha: string } }>(`/git/commits/${args.baseSha}`);
  const upserts = args.files.filter((f): f is { path: string; content: string } => !("delete" in f));
  const blobs = await Promise.all(upserts.map((f) =>
    api<{ sha: string }>(`/git/blobs`, { method: "POST", body: JSON.stringify({ content: f.content, encoding: "utf-8" }) })));
  const tree = args.files.map((f) => "delete" in f
    ? { path: f.path, mode: "100644", type: "blob", sha: null }
    : { path: f.path, mode: "100644", type: "blob", sha: blobs[upserts.indexOf(f)].sha });
  const newTree = await api<{ sha: string }>(`/git/trees`, { method: "POST", body: JSON.stringify({ base_tree: baseCommit.tree.sha, tree }) });
  const commit = await api<{ sha: string }>(`/git/commits`, {
    method: "POST", body: JSON.stringify({ message: args.message, tree: newTree.sha, parents: [args.baseSha] }),
  });
  await createBranchViaApp({ token: args.token, repo: args.repo, branch: args.branch, fromSha: commit.sha, fetchImpl: args.fetchImpl });
}

export type RepoState = "absent" | "m8t-brain" | "empty" | "foreign";

/** A minimal repo-content backend so classification is auth-agnostic + testable. */
export interface RepoProbe {
  /** HTTP status of GET /repos/{owner}/{repo} — 200 exists, 404 absent. */
  repoStatus(): Promise<number>;
  /** Decoded text of a repo file, or null on 404. */
  getFile(path: string): Promise<string | null>;
  /** True iff the repo exists but has no commits ("This repository is empty"). */
  isEmpty(): Promise<boolean>;
}

/** Classify a repo for the reuse/adopt/create decision. */
export async function probeRepoState(probe: RepoProbe): Promise<RepoState> {
  const status = await probe.repoStatus();
  if (status === 404) return "absent";
  if (status !== 200) {
    throw new LocalCliError({
      code: "GH_REPO_PROBE_FAILED",
      message: `GET repo returned HTTP ${status.toString()} (expected 200 or 404)`,
    });
  }
  const marker = await probe.getFile(".m8t/brain.yaml");
  if (marker !== null && isM8tBrainMarker(marker)) return "m8t-brain";
  if (await probe.isEmpty()) return "empty";
  return "foreign";
}

/** A RepoProbe backed by a GitHub App installation token (the installer/App path). */
export function appRepoProbe(args: { token: string; repo: string; fetchImpl?: typeof fetch }): RepoProbe {
  const doFetch = args.fetchImpl ?? fetch;
  const H = ghHeaders(args.token);
  return {
    repoStatus: async () => {
      const res = await doFetch(`${GH_API}/repos/${args.repo}`, { headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
      return res.status;
    },
    getFile: (path) => readRepoFileViaApp({ token: args.token, repo: args.repo, path, fetchImpl: args.fetchImpl }),
    isEmpty: async () => {
      // The Contents API on the repo root returns 404 ("This repository is empty")
      // for a repo with no commits.
      const res = await doFetch(`${GH_API}/repos/${args.repo}/contents`, { headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
      return res.status === 404;
    },
  };
}

export interface OpenPr { number: number; htmlUrl: string }

/** Open a new pull request via the Pulls API. */
export async function openPullRequestViaApp(args: {
  token: string; repo: string; base: string; head: string; title: string; body: string; fetchImpl?: typeof fetch;
}): Promise<OpenPr> {
  const doFetch = args.fetchImpl ?? fetch;
  const res = await doFetch(`${GH_API}/repos/${args.repo}/pulls`, {
    method: "POST", headers: ghHeaders(args.token), signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS),
    body: JSON.stringify({ base: args.base, head: args.head, title: args.title, body: args.body }),
  });
  const text = await res.text();
  if (!res.ok) throw new LocalCliError({ code: "GH_APP_PR_OPEN_FAILED", message: `POST /pulls HTTP ${res.status.toString()}: ${text.slice(0, 300)}` });
  const j = JSON.parse(text) as { number: number; html_url: string };
  return { number: j.number, htmlUrl: j.html_url };
}

/** Find an existing OPEN pull request for `head` (the m8t seed-update branch), or null if none. */
export async function findOpenPr(args: { token: string; repo: string; head: string; fetchImpl?: typeof fetch }): Promise<OpenPr | null> {
  const doFetch = args.fetchImpl ?? fetch;
  const owner = args.repo.split("/")[0];
  const res = await doFetch(`${GH_API}/repos/${args.repo}/pulls?state=open&head=${owner}:${args.head}`, { headers: ghHeaders(args.token), signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS) });
  if (!res.ok) return null;
  const list = JSON.parse(await res.text()) as { number: number; html_url: string }[];
  return list.length > 0 ? { number: list[0].number, htmlUrl: list[0].html_url } : null;
}

/** Patch an existing pull request's title and/or body. */
export async function updatePullRequestViaApp(args: { token: string; repo: string; number: number; title?: string; body?: string; fetchImpl?: typeof fetch }): Promise<void> {
  const doFetch = args.fetchImpl ?? fetch;
  const patch: Record<string, string> = {};
  if (args.title !== undefined) patch.title = args.title;
  if (args.body !== undefined) patch.body = args.body;
  const res = await doFetch(`${GH_API}/repos/${args.repo}/pulls/${args.number.toString()}`, {
    method: "PATCH", headers: ghHeaders(args.token), signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS), body: JSON.stringify(patch),
  });
  if (!res.ok) throw new LocalCliError({ code: "GH_APP_PR_UPDATE_FAILED", message: `PATCH /pulls/${args.number.toString()} HTTP ${res.status.toString()}: ${(await res.text()).slice(0, 300)}` });
}

/** Close a pull request, optionally leaving an explanatory comment first (e.g. "superseded by #N"). */
export async function closePullRequestViaApp(args: { token: string; repo: string; number: number; comment?: string; fetchImpl?: typeof fetch }): Promise<void> {
  const doFetch = args.fetchImpl ?? fetch;
  const H = ghHeaders(args.token);
  if (args.comment) {
    await doFetch(`${GH_API}/repos/${args.repo}/issues/${args.number.toString()}/comments`, {
      method: "POST", headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS), body: JSON.stringify({ body: args.comment }),
    });
  }
  const res = await doFetch(`${GH_API}/repos/${args.repo}/pulls/${args.number.toString()}`, {
    method: "PATCH", headers: H, signal: AbortSignal.timeout(GH_REQUEST_TIMEOUT_MS), body: JSON.stringify({ state: "closed" }),
  });
  if (!res.ok) throw new LocalCliError({ code: "GH_APP_PR_CLOSE_FAILED", message: `PATCH /pulls/${args.number.toString()} HTTP ${res.status.toString()}: ${(await res.text()).slice(0, 300)}` });
}
